/**
 * Landing Hero organism component
 * Main hero section for the landing page with headline, description, and primary CTA
 */

// #region Imports
import { Box, Typography, Button, Container, Stack, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PlayArrow, BarChart, Settings, PrecisionManufacturing } from "@mui/icons-material";
import { Link } from "@tanstack/react-router";
// #endregion

// #region Types
/**
 * Props for LandingHero organism component
 */
interface LandingHeroOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingHero organism component for the main hero section
 * Features headline, value proposition, and primary call-to-action
 *
 * @param props - The component props
 * @returns JSX element representing the hero section
 */
function LandingHeroOrganism({ ...props }: LandingHeroOrganismProps) {
    const { t } = useTranslation("landing");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <Box
            component="section"
            role="banner"
            aria-label="Hero section"
            sx={{
                minHeight: { xs: "calc(100vh - 56px)", md: "calc(100vh - 64px)" },
                backgroundColor: "background.default",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                "&::before": {
                    content: '""',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "background.default",
                    pointerEvents: "none",
                },
            }}
            {...props}
        >
            <Container
                maxWidth="lg"
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
            >
                <Box
                    sx={{
                        zIndex: 1,
                        textAlign: { xs: "center", md: "left" },
                        maxWidth: { md: "60%" },
                        mx: { xs: "auto", md: 0 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: { xs: "center", md: "flex-start" },
                    }}
                >
                    {/* Badge */}
                    <Box
                        role="img"
                        aria-label={t("hero.badge")}
                        sx={[
                            {
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                px: 2,
                                py: 1,
                                mb: 3,
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                borderRadius: 2,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                border: "none",
                            },
                            theme =>
                                theme.applyStyles("dark", {
                                    backgroundColor: "rgba(100, 116, 139, 0.9)",
                                    color: "#FFFFFF",
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                }),
                        ]}
                    >
                        <PrecisionManufacturing fontSize="small" aria-hidden="true" />
                        {t("hero.badge")}
                    </Box>

                    {/* Headline */}
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={[
                            {
                                mb: 3,
                                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
                                fontWeight: 700,
                                lineHeight: 1.1,
                                color: theme.palette.text.primary,
                                textShadow: "none",
                            },
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "#FFFFFF",
                                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                                }),
                        ]}
                    >
                        <Box component="span" sx={{ display: "block" }}>
                            {t("hero.title.main")}
                        </Box>
                        <Box
                            component="span"
                            sx={[
                                {
                                    color: theme.palette.secondary.main,
                                    textShadow: "none",
                                },
                                theme =>
                                    theme.applyStyles("dark", {
                                        textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                                    }),
                            ]}
                        >
                            {t("hero.title.accent")}
                        </Box>
                    </Typography>

                    {/* Subtitle */}
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={[
                            {
                                mb: 4,
                                fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
                                fontWeight: 400,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.4,
                                maxWidth: 600,
                                textShadow: "none",
                            },
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.9)",
                                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                }),
                        ]}
                    >
                        {t("hero.subtitle")}
                    </Typography>

                    {/* Feature highlights */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                        sx={[
                            {
                                mb: 5,
                                "& > *": {
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: theme.palette.text.secondary,
                                    fontSize: "0.95rem",
                                    textShadow: "none",
                                },
                            },
                            theme =>
                                theme.applyStyles("dark", {
                                    "& > *": {
                                        color: "rgba(255, 255, 255, 0.85)",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                    },
                                }),
                        ]}
                        role="list"
                        aria-label="Key benefits"
                    >
                        <Box role="listitem">
                            <BarChart color="primary" fontSize="small" aria-hidden="true" />
                            <span>{t("hero.features.wasteReduction")}</span>
                        </Box>
                        <Box role="listitem">
                            <Settings color="primary" fontSize="small" aria-hidden="true" />
                            <span>{t("hero.features.multipleMethods")}</span>
                        </Box>
                    </Stack>

                    {/* Call to Action */}
                    <Stack direction="column" spacing={2} sx={{ alignItems: "center" }}>
                        <Button
                            id="hero-section-generate"
                            component={Link}
                            to="/generate"
                            variant="contained"
                            size="large"
                            startIcon={<PlayArrow aria-hidden="true" />}
                            aria-label="Start optimizing your cutting layouts now"
                            sx={{
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                py: 1.5,
                                px: 4,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                "&:hover": {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    transform: "translateY(-2px)",
                                    boxShadow: theme.shadows[8],
                                },
                                "&:focus": {
                                    outline: `3px solid ${theme.palette.primary.light}`,
                                    outlineOffset: 2,
                                },
                                transition: "all 0.3s ease",
                            }}
                            data-testid="start-optimizing-button"
                        >
                            {t("hero.cta.primary")}
                        </Button>

                        <Typography
                            variant="body2"
                            sx={[
                                {
                                    color: theme.palette.text.secondary,
                                    fontStyle: "italic",
                                    textShadow: "none",
                                },
                                theme =>
                                    theme.applyStyles("dark", {
                                        color: "rgba(255, 255, 255, 0.8)",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                                    }),
                            ]}
                        >
                            {t("hero.cta.subtitle")}
                        </Typography>
                    </Stack>
                </Box>

                {/* Visual Element - Right side */}
                {!isMobile && (
                    <Box
                        sx={{
                            width: { md: 350, lg: 450 },
                            height: { md: 350, lg: 450 },
                            opacity: 0.5,
                            pointerEvents: "none",
                        }}
                        aria-hidden="true"
                    >
                        <Box
                            component="img"
                            src="/images/cutting-board-layout-light.svg"
                            alt="Decorative cutting board layout illustration"
                            sx={[
                                {
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                },
                                theme =>
                                    theme.applyStyles("dark", {
                                        display: "none",
                                    }),
                            ]}
                        />
                        <Box
                            component="img"
                            src="/images/cutting-board-layout-dark.svg"
                            alt="Decorative cutting board layout illustration"
                            sx={[
                                {
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "none",
                                },
                                theme =>
                                    theme.applyStyles("dark", {
                                        display: "block",
                                    }),
                            ]}
                        />
                    </Box>
                )}
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingHeroOrganism;
export type { LandingHeroOrganismProps };
// #endregion
